import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Alert,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Paper,
  Chip,
  Switch,
  FormControlLabel,
  MenuItem
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { addDocument, getCollection, updateDocument, deleteDocument } from '../../services/firestoreService';

const ManageNeeds = () => {
  const [needName, setNeedName] = useState('');
  const [needCode, setNeedCode] = useState('');
  const [needDescription, setNeedDescription] = useState('');
  const [needGoal, setNeedGoal] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [needs, setNeeds] = useState([]);
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [editingNeed, setEditingNeed] = useState(null);

  useEffect(() => {
    fetchNeeds();
  }, []);

  const fetchNeeds = async () => {
    try {
      const [needsData, goalsData] = await Promise.all([getCollection('needs'), getCollection('goals')]);
      setNeeds(needsData || []);
      setGoals(goalsData || []);
    } catch (error) {
      console.error('Error fetching needs:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const needData = {
        name: needName.trim(),
        code: needCode.trim().toLowerCase().replace(/\s+/g, '_'),
        description: needDescription.trim(),
        goalName: needGoal || null,
        isActive: isActive,
        createdAt: editingNeed ? editingNeed.createdAt : new Date(),
        updatedAt: new Date()
      };

      if (editingNeed) {
        await updateDocument('needs', editingNeed.id, needData);
        setSnackbar({
          open: true,
          message: 'Necesidad actualizada exitosamente',
          severity: 'success'
        });
        setEditingNeed(null);
      } else {
        await addDocument('needs', needData);
        setSnackbar({
          open: true,
          message: 'Necesidad agregada exitosamente',
          severity: 'success'
        });
      }

      // Limpiar formulario
      setNeedName('');
      setNeedCode('');
      setNeedDescription('');
      setNeedGoal('');
      setIsActive(true);

      // Actualizar lista
      fetchNeeds();
    } catch (error) {
      console.error('Error saving need:', error);
      setSnackbar({
        open: true,
        message: 'Error al guardar la necesidad',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (need) => {
    setEditingNeed(need);
    setNeedName(need.name);
    setNeedCode(need.code);
    setNeedDescription(need.description || '');
    setNeedGoal(need.goalName || '');
    setIsActive(need.isActive !== undefined ? need.isActive : true);
  };

  const handleCancelEdit = () => {
    setEditingNeed(null);
    setNeedName('');
    setNeedCode('');
    setNeedDescription('');
    setNeedGoal('');
    setIsActive(true);
  };

  const handleDelete = async (needId) => {
    if (window.confirm('¿Estás seguro de eliminar esta necesidad?')) {
      try {
        await deleteDocument('needs', needId);
        setSnackbar({
          open: true,
          message: 'Necesidad eliminada exitosamente',
          severity: 'success'
        });
        fetchNeeds();
      } catch (error) {
        console.error('Error deleting need:', error);
        setSnackbar({
          open: true,
          message: 'Error al eliminar la necesidad',
          severity: 'error'
        });
      }
    }
  };

  const handleToggleActive = async (need) => {
    try {
      await updateDocument('needs', need.id, {
        isActive: !need.isActive,
        updatedAt: new Date()
      });
      setSnackbar({
        open: true,
        message: `Necesidad ${!need.isActive ? 'activada' : 'desactivada'} exitosamente`,
        severity: 'success'
      });
      fetchNeeds();
    } catch (error) {
      console.error('Error updating need:', error);
      setSnackbar({
        open: true,
        message: 'Error al actualizar la necesidad',
        severity: 'error'
      });
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleSeedDefaultNeeds = async () => {
    if (!window.confirm('¿Deseas cargar las necesidades predefinidas? Esto agregará las necesidades de atención más comunes.')) {
      return;
    }

    setLoading(true);
    const defaultNeeds = [
      { name: 'Cirugía Urgente', code: 'cirugia_urgente', description: 'Procedimiento quirúrgico de emergencia' },
      { name: 'Cirugía Electiva', code: 'cirugia_electiva', description: 'Procedimiento quirúrgico programado' },
      { name: 'Esterilización', code: 'esterilizacion', description: 'Castración o esterilización' },
      { name: 'Consulta Especialidad', code: 'consulta_especialidad', description: 'Consulta con médico especialista' },
      { name: 'Consulta General', code: 'consulta_general', description: 'Consulta médica general' },
      { name: 'Exámenes', code: 'examenes', description: 'Exámenes de laboratorio o diagnóstico' },
      { name: 'Peluquería', code: 'peluqueria', description: 'Servicios de estética y peluquería' },
      { name: 'Hospitalización', code: 'hospitalizacion', description: 'Internación del paciente' },
      { name: 'Vacunación', code: 'vacunacion', description: 'Aplicación de vacunas' },
      { name: 'Desparasitación', code: 'desparasitacion', description: 'Tratamiento antiparasitario' },
      { name: 'Control Preventivo', code: 'control_preventivo', description: 'Chequeo médico preventivo' },
      { name: 'Emergencia', code: 'emergencia', description: 'Atención de emergencia' }
    ];

    try {
      for (const need of defaultNeeds) {
        await addDocument('needs', {
          ...need,
          isActive: true,
          createdAt: new Date()
        });
      }
      setSnackbar({
        open: true,
        message: `${defaultNeeds.length} necesidades predefinidas agregadas exitosamente`,
        severity: 'success'
      });
      fetchNeeds();
    } catch (error) {
      console.error('Error seeding needs:', error);
      setSnackbar({
        open: true,
        message: 'Error al cargar necesidades predefinidas',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Gestión de Necesidades de Atención
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Define y administra los tipos de necesidades de atención disponibles en el sistema
      </Typography>
      <Paper sx={{ p: 3, mt: 3, bgcolor: 'background.default' }}>
        <Box component="form" onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid
              size={{
                xs: 12,
                sm: 6
              }}>
              <TextField
                fullWidth
                label="Nombre de la Necesidad"
                value={needName}
                onChange={(e) => setNeedName(e.target.value)}
                required
                placeholder="Ej: Consulta General"
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                sm: 6
              }}>
              <TextField
                fullWidth
                label="Código"
                value={needCode}
                onChange={(e) => setNeedCode(e.target.value)}
                required
                placeholder="Ej: consulta_general"
                helperText="Identificador único (sin espacios)"
              />
            </Grid>
            <Grid size={12}>
              <TextField
                fullWidth
                label="Descripción"
                value={needDescription}
                onChange={(e) => setNeedDescription(e.target.value)}
                multiline
                rows={2}
                placeholder="Descripción breve de la necesidad"
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                sm: 6
              }}>
              <TextField
                fullWidth
                select
                label="Meta asociada"
                value={needGoal}
                onChange={(e) => setNeedGoal(e.target.value)}
                helperText="Vincula esta necesidad a una meta del Hospital"
              >
                <MenuItem value="">Sin meta asociada</MenuItem>
                {goals
                  .filter((g) => g.isActive !== false)
                  .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                  .map((goal) => (
                    <MenuItem key={goal.id} value={goal.name}>
                      {goal.name}
                    </MenuItem>
                  ))}
              </TextField>
            </Grid>
            <Grid size={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={isActive}
                    onChange={(e) => setIsActive(e.target.checked)}
                  />
                }
                label="Activa"
              />
            </Grid>
            <Grid size={12}>
              <Button
                type="submit"
                variant="contained"
                disabled={loading}
                sx={{ mr: 1 }}
              >
                {loading ? 'Guardando...' : editingNeed ? 'Actualizar Necesidad' : 'Agregar Necesidad'}
              </Button>
              {editingNeed && (
                <Button
                  variant="outlined"
                  onClick={handleCancelEdit}
                >
                  Cancelar
                </Button>
              )}
            </Grid>
          </Grid>
        </Box>
      </Paper>
      {/* Botón de carga rápida */}
      <Box sx={{ mt: 2 }}>
        <Button
          variant="outlined"
          onClick={handleSeedDefaultNeeds}
          disabled={loading}
          size="small"
        >
          Cargar Necesidades Predefinidas
        </Button>
      </Box>
      {/* Lista de necesidades */}
      <Box sx={{ mt: 4 }}>
        <Typography variant="h6" gutterBottom>
          Necesidades Registradas ({needs.length})
        </Typography>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Nombre</TableCell>
                <TableCell>Código</TableCell>
                <TableCell>Descripción</TableCell>
                <TableCell>Meta asociada</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell align="right">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {needs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    No hay necesidades registradas
                  </TableCell>
                </TableRow>
              ) : (
                needs.map((need) => (
                  <TableRow key={need.id}>
                    <TableCell>{need.name}</TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                        {need.code}
                      </Typography>
                    </TableCell>
                    <TableCell>{need.description || 'Sin descripción'}</TableCell>
                    <TableCell>
                      {need.goalName ? (
                        <Chip label={need.goalName} size="small" color="primary" />
                      ) : (
                        <Typography variant="caption" color="text.secondary">Sin meta</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Switch
                        checked={need.isActive !== undefined ? need.isActive : true}
                        onChange={() => handleToggleActive(need)}
                        size="small"
                      />
                      <Chip
                        label={need.isActive ? 'Activa' : 'Inactiva'}
                        color={need.isActive ? 'success' : 'default'}
                        size="small"
                        sx={{ ml: 1 }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => handleEdit(need)}
                        color="primary"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(need.id)}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ManageNeeds;
